import React from "react";
import PrimaryButton from "../Components/PrimaryButton";
import TextButton from "../Components/TextButton";
import { useForm, Controller } from "react-hook-form";
import InputMask from "react-input-mask";
import { isValidName, isValidAge, isValidDate, isValidPhoneNumber, isValidEmail } from '../helpers';
import { useSelector } from 'react-redux';
import { Link } from "react-router-dom";
import { getContent } from "../content";

function CurrentPersonalInformationForm({ submit, cancel }) {
  const registrant = useSelector((state) => state.registrant);
  const partner = useSelector((state) => state.partner);
  const information = useSelector((state) => state.information);
  const currentLanguage = useSelector((state) => state.settings.currentLanguage);

  const { handleSubmit, control, formState: { errors }, setValue, getValues, watch } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      middle_name: '',
      suffix: '',
      birthdate: '',
      email_address: '',
      email_address_optin: false,
      phone_number: '',
      phone_type: 'cell', //  cell, home, work
      phone_number_optin: false,
      race: '',
      sex: '',
      party_affiliation: '',
      party_affiliation_other: '',
    }
  });

  const { party_affiliation } = watch();

  React.useEffect(() => {
    const {
      first_name,
      last_name,
      middle_name,
      suffix,
      birthdate,
      email_address,
      email_address_optin,
      phone_number,
      phone_type,
      phone_number_optin,
      race,
      sex,
      party_affiliation,
      party_affiliation_other,
    } = registrant;


    setValue('first_name', first_name);
    setValue('last_name', last_name);
    setValue('middle_name', middle_name);
    setValue('suffix', suffix);
    setValue('birthdate', birthdate);
    setValue('sex', sex);
    setValue('race', race);
    setValue('email_address', email_address);
    setValue('email_address_optin', email_address_optin);
    setValue('phone_number', phone_number);
    setValue('phone_type', phone_type);
    setValue('phone_number_optin', phone_number_optin);
    setValue('party_affiliation', party_affiliation);
    setValue('party_affiliation_other', party_affiliation_other);

  }, [registrant]);

  React.useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === 'party_affiliation' && type === 'change') {
        setValue('party_affiliation_other', '');
      }
    });

    return () => subscription.unsubscribe()
  }, [watch]);

  const onSubmit = data => submit(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex ">
        <div className="flex-1 p-2">
          <label>{getContent('fields.first_name')}*</label>
          <Controller
            name="first_name"
            control={control}
            rules={{
              required: true,
              validate: {
                name: value => !value || (value && isValidName(value) || getContent('validation.first_name'))
              }
            }}
            render={({ field }) => <input {...field} placeholder="Jane" type="text" autoComplete="off" maxLength="30" className={`${errors.first_name ? 'error' : ''}`} />}
          />
          {errors.first_name?.type === 'required' && <span className="error">{getContent('validation.first_name')}</span>}
          {errors.first_name?.type === 'name' && <span className="error">{getContent('validation.first_name')}</span>}
        </div>
        <div className="flex-1 p-2">
          <label>{getContent('fields.last_name')}*</label>
          <Controller
            name="last_name"
            control={control}
            rules={{
              required: true,
              validate: {
                name: value => !value || (value && isValidName(value) || getContent('validation.last_name'))
              }
            }}
            render={({ field }) => <input {...field} placeholder="Doe" type="text" autoComplete="off" maxLength="30" className={`${errors.last_name ? 'error' : ''}`} />}
          />
          {errors.last_name?.type === 'required' && <span className="error">{getContent('validation.last_name')}</span>}
          {errors.last_name?.type === 'name' && <span className="error">{getContent('validation.last_name')}</span>}
        </div>
      </div>
      <div className="flex ">
        <div className="flex-1 p-2">
          <label>{getContent('fields.middle_name')}</label>
          <Controller
            name="middle_name"
            control={control}
            rules={{
              validate: {
                name: value => !value || (value && isValidName(value) || getContent('validation.middle_name'))
              }
            }}
            render={({ field }) => <input {...field} placeholder="Henry" maxLength="30" type="text" autoComplete="off" className={`${errors.middle_name ? 'error' : ''}`} />}
          />
          {errors.middle_name?.type === 'name' && <span className="error">{getContent('validation.middle_name')}</span>}
        </div>
        <div className="flex-1 p-2">
          <label>{getContent('fields.suffix')}</label>
          <Controller
            name="suffix"
            control={control}
            render={({ field }) => {
              return (
                <div className="select">
                  <select {...field}>
                    <option value="">{getContent('placeholders.dropdown')}</option>
                    {Object.keys(information?.suffixes).map((key, i) => {
                      return (
                        <option key={i} value={key}>{information?.suffixes[key][currentLanguage]}</option>
                      );
                    })}
                  </select>
                </div>
              );
            }}
          />
        </div>
      </div>
      <div className="flex-1 p-2">
        <label>{getContent('fields.birth_date')}*</label>
        <Controller
          name="birthdate"
          control={control}
          rules={{
            required: true,
            validate: {
              dob: value => (isValidDate(value) && isValidAge(value)) || getContent('validation.birth_date_valid')
            }
          }}
          render={({ field }) => {
            return (
              <InputMask placeholder={getContent('placeholders.birth_date')} className={`input ${errors.birthdate ? 'error' : ''}`} {...field} mask="99/99/9999" />
            );
          }}
        />
        {errors.birthdate?.type === 'required' && <span className="error">{getContent('validation.birth_date')}</span>}
        {errors.birthdate?.type === 'dob' && <span className="error">{getContent('validation.birth_date_valid')}</span>}
      </div>
      <div className="flex">

        <div className="flex-1 p-2">
          <label>{getContent('fields.sex')}</label>
          <Controller
            name="sex"
            control={control}
            render={({ field }) => {
              return (
                <div className="select">
                  <select {...field}>
                    <option value="">{getContent('placeholders.dropdown')}</option>
                    {Object.keys(information?.genders).map((key, i) => {
                      return (
                        <option key={i} value={key}>{information?.genders[key][currentLanguage]}</option>
                      );
                    })}
                  </select>
                </div>
              );
            }}
          />
        </div>
        <div className="flex-1 p-2 ">
          <label>{getContent('fields.race')}</label>
          <Controller
            name="race"
            control={control}
            render={({ field }) => {
              return (
                <div className="select">
                  <select {...field}>
                    <option value="">{getContent('placeholders.dropdown')}</option>
                    {Object.keys(information?.races).map((key, i) => {
                      return (
                        <option key={i} value={key}>{information?.races[key][currentLanguage]}</option>
                      );
                    })}
                  </select>
                </div>
              );
            }}
          />
        </div>
      </div>

      <div className="flex">
        <div className="flex-1 p-2 ">
          <label>{getContent('fields.political_party')}</label>
          <Controller
            name="party_affiliation"
            control={control}
            rules={{ required: true }}
            render={({ field }) => {
              return (
                <div className={`select ${errors.party_affiliation ? 'error' : ''}`}>
                  <select {...field}>
                    <option value="">{getContent('placeholders.dropdown')}</option>
                    {Object.keys(information?.politicalParties).map((key, i) => {
                      return (
                        <option key={i} value={key}>{information?.politicalParties[key][currentLanguage]}</option>
                      );
                    })}
                  </select>
                </div>
              );
            }}
          />
          {errors.party_affiliation?.type === 'required' && <span className="error">{getContent('validation.provide_value')}</span>}

        </div>
        <div className="flex-1 p-2">
          <label>{getContent('fields.party_other')}</label>
          <Controller
            name="party_affiliation_other"
            control={control}
            rules={{
              validate: {
                other_check: value => {
                  return (((getValues('party_affiliation') === 'OTH' && value !== '') || getValues('party_affiliation') !== 'OTH') ? true : false) || getContent('validation.party_other');
                }
              }
            }}
            render={({ field }) => <input {...field} placeholder="Political Party" type="text" maxLength="50" readOnly={party_affiliation === 'OTH' ? false : true} autoComplete="off" className={`${errors.party_affiliation_other ? 'error' : ''}`} />}
          />
          {errors.party_affiliation_other?.type === 'required' && <span className="error">{getContent('validation.party_other')}</span>}
          {(errors.party_affiliation_other?.type === 'other_check') && <span className="error">{getContent('validation.party_other')}</span>}

        </div>
      </div>

      <div className="flex-1 p-2 ">
        <label>{getContent('fields.email_address')}</label>
        <Controller
          name="email_address"
          control={control}
          rules={{
            validate: {
              email: value => !value || (value && isValidEmail(value) || getContent('validation.email_address_format'))
            }
          }}
          render={({ field }) => {
            return (
              <input {...field} placeholder="you@example.com" type="text" maxLength="50" autoComplete="off" className={`${errors.email_address ? 'error' : ''}`} />
            );
          }}
        />
        {errors.email_address?.type === 'email' && <span className="error">{getContent('validation.email_address_format')}</span>}
      </div>
      <div className="flex flex-col justify-end p-2 sm:flex-row sm:justify-between sm:items-center">
        <Controller
          name="email_address_optin"
          control={control}
          render={({ field }) => <label className="checkbox"><input {...field} type="checkbox" defaultChecked={registrant.email_address_optin} /><span></span> <span>{partner['email_optin_' + currentLanguage]}</span></label>}
        />
        <Link className="text-xs underline" to="/privacy-policy" target={'_blank'}>{getContent('general.privacy_policy')}</Link>
      </div>
      <div className="flex">
        <div className="flex-1 p-2">
          <label>{getContent('fields.phone_number')}</label>
          <Controller
            name="phone_number"
            control={control}
            rules={{
              validate: {
                phone: value => !value || (value && isValidPhoneNumber(value) || getContent('validation.phone_number_valid'))
              }
            }}
            render={({ field }) => {
              return (
                <InputMask placeholder="555-555-5555" className={`input ${errors.phone_number ? 'error' : ''}`} {...field} mask="999-999-9999" />
              );
            }}
          />
          {errors.phone_number?.type === 'phone' && <span className="error">{getContent('validation.phone_number_valid')}</span>}
        </div>
        <div className="flex-1 p-2">
          <label>&nbsp;</label>
          <Controller
            name="phone_type"
            control={control}
            rules={{
              required: !getValues('phone_number') ? false : true
            }}
            render={({ field }) => {
              return (
                <div className={`select ${errors.phone_type ? 'error' : ''}`} >
                  <select {...field}>
                    <option value="" disabled>{getContent('fields.phone_type_select_label')}</option>
                    <option value="">{getContent('fields.phone_type_select')}</option>
                    <option value="cell">{getContent('fields.cell_phone')}</option>
                    <option value="home">{getContent('fields.home_phone')}</option>
                    <option value="work">{getContent('fields.work_phone')}</option>
                  </select>
                </div>
              );
            }}
          />
          {errors.phone_type?.type === 'required' && <span className="error">{getContent('validation.phone_type')}</span>}
        </div>
      </div>
      <div className="flex flex-col justify-end p-2 sm:flex-row sm:justify-between sm:items-center">
        <Controller
          name="phone_number_optin"
          control={control}
          render={({ field }) => <label className="checkbox"><input {...field} type="checkbox" defaultChecked={registrant.phone_number_optin} /><span></span> <span>{partner['phone_optin_' + currentLanguage]}</span></label>}
        />
        <Link className="text-xs underline" to="/privacy-policy" target={'_blank'}>{getContent('general.privacy_policy')}</Link>
      </div>

      <div className="flex justify-end pt-8 pb-2">
        <TextButton onClick={cancel} className="pr-2">{getContent('buttons.cancel')}</TextButton>
        <PrimaryButton submit>{getContent('buttons.update_information')}</PrimaryButton>
      </div>
    </form>
  );
}

export default CurrentPersonalInformationForm;